import React, { useState, useRef, useEffect } from 'react';
import {
  EnvelopeIcon,
  EnvelopeOpenIcon,
  PaperClipIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon as StarOutlineIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { EmailFolder } from '../../api/emailApi';

// Dropdown menu component for email actions
const EmailActionsMenu = ({
  uid,
  starred,
  currentFolder,
  onToggleStar,
  onArchive,
  onMarkAsJunk,
  onDelete
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAction = (action) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
      >
        <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 min-w-[160px]">
          {/* Star/Unstar */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(() => onToggleStar?.(uid, !starred));
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            {starred ? (
              <>
                <StarSolidIcon className="h-4 w-4 text-yellow-500" />
                <span>Unstar</span>
              </>
            ) : (
              <>
                <StarOutlineIcon className="h-4 w-4 text-gray-400" />
                <span>Star</span>
              </>
            )}
          </button>

          {/* Archive (not shown in Archive folder) */}
          {currentFolder !== EmailFolder.ARCHIVE && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAction(() => onArchive?.(uid));
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <ArchiveBoxIcon className="h-4 w-4 text-gray-400" />
              <span>Archive</span>
            </button>
          )}

          {/* Mark as Junk (not shown in Junk folder) */}
          {currentFolder !== EmailFolder.JUNK && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAction(() => onMarkAsJunk?.(uid));
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <ExclamationTriangleIcon className="h-4 w-4 text-gray-400" />
              <span>Mark as junk</span>
            </button>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 my-1"></div>

          {/* Delete */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(() => onDelete?.(uid, currentFolder === EmailFolder.TRASH));
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4" />
            <span>{currentFolder === EmailFolder.TRASH ? 'Delete permanently' : 'Delete'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

const EmailList = ({
  emails,
  loading,
  selectedEmailUid,
  onSelectEmail,
  searchTerm,
  setSearchTerm,
  page,
  totalPages,
  totalEmails,
  onPageChange,
  onRefresh,
  currentFolder,
  onToggleStar,
  onDelete,
  onArchive,
  onMarkAsJunk,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredEmails = (emails || []).filter((email) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const from = email.from || email.fromName || email.fromAddress || '';
    const fromEmail = email.fromEmail || email.fromAddress || '';
    const subject = email.subject || '';
    const preview = email.preview || '';
    return (
      from.toLowerCase().includes(term) ||
      fromEmail.toLowerCase().includes(term) ||
      subject.toLowerCase().includes(term) ||
      preview.toLowerCase().includes(term)
    );
  });

  const getDisplayName = (email) => {
    return email.from || email.fromName || email.fromAddress || email.fromEmail || 'Unknown';
  };

  const isRead = (email) => {
    return email.read ?? email.isRead ?? false;
  };

  const isStarred = (email) => {
    return email.starred ?? email.isStarred ?? false;
  };

  const getUid = (email) => {
    return email.uid ?? email.messageUid;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Email List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <EnvelopeIcon className="h-12 w-12 mb-2" />
            <p>No emails found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredEmails.map((email) => {
              const uid = getUid(email);
              const read = isRead(email);
              const starred = isStarred(email);
              
              return (
                <div
                  key={uid || email.id}
                  className={`relative px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedEmailUid === uid ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  } ${!read ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Read/Unread Icon */}
                    <div
                      className="flex-shrink-0 mt-1"
                      onClick={() => onSelectEmail(uid)}
                    >
                      {read ? (
                        <EnvelopeOpenIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EnvelopeIcon className="h-5 w-5 text-blue-600" />
                      )}
                    </div>

                    {/* Email Content */}
                    <div
                      className="flex-1 min-w-0"
                      onClick={() => onSelectEmail(uid)}
                    >
                      <div className="flex items-center justify-between">
                        <p
                          className={`text-sm truncate ${
                            !read ? 'font-semibold text-gray-900' : 'text-gray-700'
                          }`}
                        >
                          {getDisplayName(email)}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          {starred && (
                            <StarSolidIcon className="h-4 w-4 text-yellow-500" />
                          )}
                          <span className="text-xs text-gray-500">
                            {formatDate(email.date || email.receivedAt || email.receivedAtUtc)}
                          </span>
                        </div>
                      </div>
                      <p
                        className={`text-sm truncate ${
                          !read ? 'font-medium text-gray-900' : 'text-gray-600'
                        }`}
                      >
                        {email.subject || '(No Subject)'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-500 truncate flex-1">{email.preview}</p>
                        {(email.hasAttachments || email.attachmentCount > 0) && (
                          <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    {/* Three-dot menu */}
                    <div className="flex-shrink-0">
                      <EmailActionsMenu
                        uid={uid}
                        starred={starred}
                        currentFolder={currentFolder}
                        onToggleStar={onToggleStar}
                        onArchive={onArchive}
                        onMarkAsJunk={onMarkAsJunk}
                        onDelete={onDelete}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-700">
            {totalEmails} email{totalEmails !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailList;
